import * as THREE from 'three';
import AbstractObject from '../../../misc/object';

import { addDistanceLabel } from '../../../../services/helper.service';
import { HTMLEditableLabel } from '../../../objects/html-label';
import Arrow from '../../../objects/arrow';

import Ground, { LabelsPadding } from '../../../objects/ground';
import { Screw } from '../../../objects/screw';

import { unitTransform } from '@services/helper.service';
import { defaultUnits } from '@config';

import Scene from "@scene";

export default class Reinforcement extends AbstractObject {

  constructor(options) {
    super(options);

    const ground = new Ground({
      type: Ground.types.BASE,
      width: this.size.width,
      height: this.size.height,
      depth: this.size.depth,
      padding: {
        C1y: null,
        C1x: null,
        C2y: null,
        C2x: null
      }
    });

    ground.mesh.children[0].material = ground.mesh.children[0].material.clone();
    ground.mesh.children[0].material.color = new THREE.Color(0x688eb7);
    ground.mesh.children[0].material.transparent = true;
    ground.mesh.children[0].material.opacity = 0.4;
    ground.mesh.children[0].material.depthTest = true;
    // Disable depth writing to remove the clipping effect of the rebar front/back sides
    ground.mesh.children[0].material.depthWrite = false; 
    
    ground.rotation.z = Math.PI / 2;

    this.object.addObject(ground);

    // Calculate screws count per line
    this.options.screw.count.m = Math.ceil(
      (this.size.width - this.options.screw.shift.c1) / (this.options.screw.padding.s + this.options.screw.radius / 2)
    );
    this.generateScrews(this.getSquarePanelScrewCoords(this.options.screw));
  }

  get size() {
    return {
      width: this.options.width,
      height: this.options.height,
      depth: this.options.depth
    };
  }

  getSquarePanelScrewCoords({ count, padding }) {
    const coordinates = [];

    for (let n = 0; n < count.n; n++) {
      for (let m = 0; m < count.m; m++) {

        const position = new THREE.Vector3();

        position.x = m * padding.s;
        position.y = n * padding.d;
        position.z = this.size.depth / 2 + this.options.screw.headSize;

        coordinates.push(position);
      }
    }

    return coordinates;
  }

  generateScrews(coords) {
    const screwLayer = new THREE.Object3D();
    screwLayer.position.x = this.size.width / -2 + this.options.screw.radius + this.options.screw.shift.c1;
    screwLayer.position.y = this.size.height / -2 + this.options.screw.radius + this.options.screw.shift.c;

    coords.forEach((item) => {
      const screw = new Screw({
        height: this.size.depth + this.options.screw.headSize,
        radius: this.options.screw.radius
      });
      screw.mesh.rotation.y = Math.PI / 2;
      screw.setPosition(item);

      screwLayer.add(screw.mesh);
    });

    this.object.addMesh(screwLayer);
  }

  createWidthLabel(text = 'Wb', options) {
    const {object, text: domElement} = addDistanceLabel({
      top: {
        from: {
          x: this.size.width / -2,
          y: this.size.height / 2,
          z: this.size.depth
        },
        to: {
          x: this.size.width / -2,
          y: this.size.height / 2 + LabelsPadding * 2,
          z: this.size.depth
        }
      },
      bottom: {
        from: {
          x: this.size.width / 2,
          y: this.size.height / 2,
          z: this.size.depth
        },
        to: {
          x: this.size.width / 2,
          y: this.size.height / 2 + LabelsPadding * 2,
          z: this.size.depth
        }
      },
      size: this.size.width,
      arrowDirection: Arrow.types.direction.X,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : '') + 
        `${unitTransform(+this.size.width).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.size.width).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          reinforcement: {
            width: {
              value: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      },
      arrowSize: 25
    });
    this.object.addMesh(object);

    return {domElement};
  }

  createHeightLabel(text = 'Hb', options) {
    const position = {
      top: {
        from: {
          x: this.size.width / 2,
          y: this.size.height / -2,
          z: this.size.depth
        },
        to: {
          x: this.size.width / 2 + LabelsPadding * 2,
          y: this.size.height / -2,
          z: this.size.depth
        }
      },
      bottom: {
        from: {
          x: this.size.width / 2,
          y: this.size.height / 2,
          z: this.size.depth
        },
        to: {
          x: this.size.width / 2 +  LabelsPadding * 2,
          y: this.size.height / 2,
          z: this.size.depth
        }
      }
    };
    const {object, text: domElement} = addDistanceLabel({
      ...position,
      size: this.size.height,
      arrowDirection: Arrow.types.direction.Y,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : '') + 
        `${unitTransform(+this.size.height).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.size.height).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.LEFT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          reinforcement: {
            height: {
              value: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      },
      arrowSize: 25
    });
    this.object.addMesh(object);

    return {domElement, position, object};
  }

  createDLabel(text = 'D', options) {
    const x = this.size.width / -2 + this.options.screw.shift.c1;
    const y = this.size.height / -2 + this.options.screw.shift.c;

    const padding = LabelsPadding * 2;

    const position = {
      top: {
        from: {
          x,
          y: y + this.options.screw.radius * 2,
          z: this.size.depth
        },
        to: {
          x: x - padding - this.options.screw.shift.c1,
          y: y + this.options.screw.radius * 2,
          z: this.size.depth
        }
      },
      bottom: {
        from: {
          x,
          y: y + this.options.screw.padding.d,
          z: this.size.depth
        },
        to: {
          x: x - padding - this.options.screw.shift.c1,
          y: y + this.options.screw.padding.d,
          z: this.size.depth
        }
      }
    };

    const {object, text: domElement} = addDistanceLabel({
      ...position,
      size: this.options.screw.padding.d - this.options.screw.radius * 2,
      arrowDirection: Arrow.types.direction.Y,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : '') + 
        `${unitTransform(+this.options.screw.padding.d).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.options.screw.padding.d).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          reinforcement: {
            screw: {
              padding: {
                d: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
              }
            }
          }
        },
        false,
        didChange);
      },
      arrowSize: 25
    });
    this.object.addMesh(object);
    return {domElement, position, object};
  }

  createSLabel(text = 'S', options) {
    const x = this.size.width / -2 + this.options.screw.shift.c1 + this.options.screw.radius;
    const y = this.size.height / -2 + this.options.screw.shift.c;

    const padding = LabelsPadding * 2;

    const position = {
      top: {
        from: {
          x: x + this.options.screw.radius,
          y,
          z: this.size.depth
        },
        to: {
          x: x + this.options.screw.radius,
          y: y - padding - this.options.screw.shift.c,
          z: this.size.depth
        }
      },
      bottom: {
        from: {
          x: x + this.options.screw.padding.s - this.options.screw.radius,
          y,
          z: this.size.depth
        },
        to: {
          x: x + this.options.screw.padding.s - this.options.screw.radius,
          y: y - padding - this.options.screw.shift.c,
          z: this.size.depth
        }
      }
    };

    const {object, text: domElement} = addDistanceLabel({
      ...position,
      size: this.options.screw.padding.s - this.options.screw.radius * 2,
      arrowDirection: Arrow.types.direction.X,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : '') + 
        `${unitTransform(+this.options.screw.padding.s).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.options.screw.padding.s).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.TOP,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          reinforcement: {
            screw: {
              padding: {
                s: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
              }
            }
          }
        },
        false,
        didChange);
      },
      arrowSize: 25
    });
    this.object.addMesh(object);
    return {domElement, position};
  }

  createCLabel(text = 'C', options) {
    const x = this.size.width / -2 + this.options.screw.shift.c1;
    const y = this.size.height / -2;

    const padding = LabelsPadding * 2;

    const position = {
      top: {
        from: {
          x: x - this.options.screw.shift.c1,
          y,
          z: this.size.depth
        },
        to: {
          x: x - padding - this.options.screw.shift.c1,
          y,
          z: this.size.depth
        }
      },
      bottom: {
        from: {
          x,
          y: y + this.options.screw.shift.c,
          z: this.size.depth
        },
        to: {
          x: x - padding - this.options.screw.shift.c1,
          y: y + this.options.screw.shift.c,
          z: this.size.depth
        }
      }
    };

    const {object, text: domElement} = addDistanceLabel({
      ...position,
      size: this.options.screw.shift.c,
      arrowDirection: Arrow.types.direction.Y,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : '') + 
        `${unitTransform(+this.options.screw.shift.c).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.options.screw.shift.c).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          reinforcement: {
            screw: {
              shift: {
                c: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
              }
            }
          }
        },
        false,
        didChange);
      },
      arrowSize: 25
    });
    this.object.addMesh(object);
    return {domElement, position};
  }

  createC1Label(text = 'C1', options) {
    const x = this.size.width / -2;
    const y = this.size.height / -2 + this.options.screw.shift.c;

    const padding = LabelsPadding * 2;

    const position = {
      top: {
        from: {
          x,
          y: y - this.options.screw.shift.c,
          z: this.size.depth
        },
        to: {
          x,
          y: y - padding - this.options.screw.shift.c,
          z: this.size.depth
        }
      },
      bottom: {
        from: {
          x: x + this.options.screw.shift.c1,
          y,
          z: this.size.depth
        },
        to: {
          x: x + this.options.screw.shift.c1,
          y: y - padding - this.options.screw.shift.c,
          z: this.size.depth
        }
      }
    };

    const {object, text: domElement} = addDistanceLabel({
      ...position,
      size: this.options.screw.shift.c1,
      arrowDirection: Arrow.types.direction.X,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : '') + 
        `${unitTransform(+this.options.screw.shift.c1).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.options.screw.shift.c1).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.TOP,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          reinforcement: {
            screw: {
              shift: {
                c1: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
              }
            }
          }
        },
        false,
        didChange);
      },
      arrowSize: 25
    });
    this.object.addMesh(object);
    return {domElement, position};
  }

}



// WEBPACK FOOTER //
// ./src/object-view/generators/rebar-reinforcement/objects/reinforcement.js